// Bezahlt-Regeln eines Teilnehmers — 1:1-Spiegel von AveOrga.Core/Services/PaymentRules.cs.
// Wird für die optimistische Anzeige und den Offline-Pfad (offlineApi.setPresence) genutzt; der Server
// bleibt maßgeblich. Änderungen an den Regeln immer auf beiden Seiten nachziehen.
//
// Enums kommen als String über die API (JsonStringEnumConverter), ältere Daten evtl. als Zahl:
// PresenceStatus Absent=0, Present=1, Other=2 · ParticipantType Confirmed=1, Waitlist=3.

const PRESENCE_BY_NUMBER = ['Absent', 'Present', 'Other'];

function normalizePresence(status) {
  if (typeof status === 'number') return PRESENCE_BY_NUMBER[status] ?? 'Other';
  return status ?? 'Other';
}

export const isPresentStatus = (status) => normalizePresence(status) === 'Present';
export const isAbsentStatus = (status) => normalizePresence(status) === 'Absent';

const isWaitlist = (p) => p?.participantType === 'Waitlist' || p?.participantType === 3;

// Spieltagleiter zahlen nie; sonst schuldet, wer einen Preis > 0 hat. Das Feld owesPayment aus der
// API-Antwort hat Vorrang (ältere Offline-Kopien haben es noch nicht -> lokale Berechnung).
export function owesPayment(p) {
  if (!p) return false;
  if (typeof p.owesPayment === 'boolean') return p.owesPayment;
  return !p.isOrganizing && Number(p.price ?? 0) > 0;
}

// Anzeige-Zustand der Preis-Zelle:
//  'paid' (grün)  — als bezahlt markiert
//  'open' (rot)   — schuldet, unbezahlt, nicht abwesend
//  'none' (grau)  — nichts zu zahlen oder No-Show (abwesend + unbezahlt zählt bewusst NICHT als offen)
export function paymentState(p) {
  if (!p) return 'none';
  if (p.isPaid) return 'paid';
  if (owesPayment(p) && !isAbsentStatus(p.presenceStatus)) return 'open';
  return 'none';
}

// Spiegel von PaymentRules.ApplyPresenceTransition für einen Anwesenheits-Wechsel:
//  - gleicher Status: keine Änderung (Present -> Present ist ein No-Op)
//  - -> Present + Auto-bezahlt an + schuldet + unbezahlt: bezahlt durch Anwesenheit
//  - Present -> anderer Status + Markierung durch Anwesenheit: Markierung entfernen (unabhängig vom
//    aktuellen Schalter und davon, ob noch geschuldet wird). Manuell gesetztes "bezahlt" bleibt.
// Liefert immer eine neue Zeile mit dem neuen presenceStatus.
export function applyPresenceWithAutoPaid(p, nextStatus, autoPaidOnPresence, atIso = new Date().toISOString()) {
  const base = { ...p, presenceStatus: nextStatus };
  const oldStatus = normalizePresence(p?.presenceStatus);
  const newStatus = normalizePresence(nextStatus);
  if (oldStatus === newStatus) return base;

  if (newStatus === 'Present') {
    if (autoPaidOnPresence && !p.isPaid && owesPayment(p)) {
      return { ...base, isPaid: true, paidByPresence: true, paidAt: atIso };
    }
  } else if (oldStatus === 'Present' && p.isPaid && p.paidByPresence) {
    return { ...base, isPaid: false, paidByPresence: false, paidAt: null };
  }
  return base;
}

// Felder, die ein Anwesenheits-Tap (Server-Antwort oder lokaler Spiegel) verändern kann.
export const PRESENCE_PAYMENT_FIELDS = ['presenceStatus', 'isPaid', 'paidByPresence', 'paidAt'];

// Kopiert nur die in src vorhandenen Anwesenheits-/Bezahlt-Felder (Snapshot bzw. Merge der Server-Antwort).
export function pickPresencePayment(src) {
  const out = {};
  if (!src) return out;
  for (const key of PRESENCE_PAYMENT_FIELDS) {
    if (key in src) out[key] = src[key];
  }
  return out;
}

// "offen"-Chip: Anzahl und Summe (in Cent) aller Nicht-Wartelisten-Teilnehmer im Zustand 'open'.
// Entspricht EventStatsIncome.OpenCents im Backend.
export function summarizeOpen(list) {
  let count = 0;
  let cents = 0;
  for (const p of (list ?? [])) {
    if (isWaitlist(p) || paymentState(p) !== 'open') continue;
    count += 1;
    cents += Math.round(Number(p.price ?? 0) * 100);
  }
  return { count, cents };
}

// Euro-Anzeige wie der Backend-Platzhalter {Betrag}: 20 -> "20,00 €".
export const formatEuro = (value) =>
  Number(value ?? 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
